import { Component, ElementRef, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { formatDate } from "@angular/common";
import { DEFAULT_URL } from "../../app-urls";
import { Subscription } from "rxjs";
import { RepositoryService } from "../../linq-utils/repository.service";
import { UserService } from "../../user/user.service";
import { InsightCard } from "../../ui-components/insight-cards/insight-cards.component";
import { ModelAnalytics, ORGANISATION_STATE } from "../../common/organisation";
import { SCREEN } from "../layouts-enums";
import { AnalyticsService } from "../../model-services/analytics-service";
import { LineData } from "../../charts/line-chart/line-chart.component";
import { getUserRoleName, User } from "../../common/user";
import { Column } from "../../ui-components/table/table-component";
import { analyticsByDay } from "../../model-services/analytics-days";
import { ChangeRow, changeRow, changesOnDay, customerActivity, OrganisationUsage, organisationsOnDay, summariseUsage, TalkingPoint, talkingPoints, UsageSummary } from "./admin-usage";

const COMPONENT = "Admin layout"

// LINQ's administration page: recent activity, statistics across organisations (with each day's changes), every
// organisation, and the usage of the one chosen, for talking to the customer about how they use LINQ
@Component({
    selector: 'app-admin-layout',
    templateUrl: './admin-layout.html',
    styleUrls: ['./admin-layout.scss'],
    standalone: false
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
    screen:SCREEN = SCREEN.SYS_ADMIN
    loading: boolean = false
    routeSub:Subscription

    analyticsData:any
    activityData:any[]
    cards:InsightCard[]
    lineData:LineData
    chartDays:Date[] = []

    // the day chosen on the statistics chart
    selectedDay:Date|undefined
    dayChanges:ChangeRow[] = []
    dayOrganisations:{organisationKey:string, organisation:string, changes:number, people:string[]}[] = []

    // the organisation chosen, and how it uses LINQ
    selectedOrganisation:any
    usage:OrganisationUsage|undefined
    summary:UsageSummary|undefined
    points:TalkingPoint[] = []
    usageCards:InsightCard[] = []
    usageLineData:LineData|undefined
    usageChanges:ChangeRow[] = []
    usageActivity:any[] = []
    usageModels:any[] = []
    usageUsers:any[] = []
    users:User[]
    loadingUsage = false
    usageError = ""

    // the panel is a mat-card, so its element is asked for rather than the card component
    @ViewChild('usagePanel', { read: ElementRef }) usagePanel?:ElementRef<HTMLElement>

    readonly organisationColumns = [
        new Column('name', 'Organisation', 'string'),
        new Column('state', 'State', 'string'),
        new Column('createdDate', 'Created', 'date'),
        new Column('lastAccess', 'Last access', 'date'),
        new Column('endDate', 'End date', 'date'),
        new Column('insightsDate', 'Insights updated', 'date'),
        new Column('isPartner', 'Partner?', 'string'),
        new Column('isExemptFromPayment', 'Exempt', 'string'),
        new Column('numUsers', 'Users', 'number'),
        new Column('numModels', 'Models', 'number'),
        new Column('numProjects', 'Projects', 'number'),
        new Column('actionNodes', 'Action nodes', 'number'),
        new Column('cost', 'Cost', 'number'),
        new Column('carbon', 'Carbon', 'number'),
    ]
    readonly activityColumns = [
        new Column('timestamp', 'Time', 'datetime'),
        new Column('name', 'Name', 'string'),
        new Column('email', 'Email', 'string'),
        new Column('organisation', 'Organisation', 'string'),
        new Column('action', 'Action', 'string'),
        new Column('who', 'By', 'string'),
    ]
    readonly dayColumns = [
        new Column('time', 'Time', 'datetime'),
        new Column('organisation', 'Organisation', 'string'),
        new Column('person', 'Person', 'string'),
        new Column('change', 'Change', 'string'),
        new Column('model', 'Model', 'string'),
        new Column('added', 'Added', 'number'),
        new Column('changed', 'Changed', 'number'),
        new Column('deleted', 'Deleted', 'number'),
    ]
    readonly usageChangeColumns = [
        new Column('time', 'Time', 'datetime'),
        new Column('person', 'Person', 'string'),
        new Column('change', 'Change', 'string'),
        new Column('model', 'Model', 'string'),
        new Column('added', 'Added', 'number'),
        new Column('changed', 'Changed', 'number'),
        new Column('deleted', 'Deleted', 'number'),
    ]
    readonly usageActivityColumns = [
        new Column('timestamp', 'Time', 'datetime'),
        new Column('name', 'Name', 'string'),
        new Column('action', 'Action', 'string'),
    ]
    readonly usageModelColumns = [
        new Column('name', 'Model', 'string'),
        new Column('function', 'Function', 'string'),
        new Column('where', 'Workspace', 'string'),
        new Column('createdDate', 'Created', 'date'),
        new Column('lastChanged', 'Last changed', 'datetime'),
        new Column('lastChangedBy', 'By', 'string'),
    ]
    readonly usageUserColumns = [
        new Column('name', 'Name', 'string'),
        new Column('email', 'Email', 'string'),
        new Column('role', 'Role', 'string'),
        new Column('lastActive', 'Last active', 'datetime'),
        new Column('status', 'Status', 'string'),
    ]

    constructor(private route:ActivatedRoute, private router:Router, private repoService:RepositoryService,
        private userService:UserService, @Inject(LOCALE_ID) public locale: string, private analyticsService:AnalyticsService) {
    }

    ngOnDestroy(): void {
        if (this.routeSub) this.routeSub.unsubscribe()
    }

    ngOnInit() {
        setTimeout(() => {
            this.routeSub = this.route.params.subscribe(async () => {
                this.loadAdminData()
            })
        })
    }

    private async loadAdminData() {
        this.loading = true
        try {
            this.analyticsData = await this.repoService.callSingleAPI(this.repoService.getData("api/admin/analytics"), COMPONENT, "Load admin data", this.userService.getUser())
            await this.loadActivityData()
            this.createData()
        }catch(err) {
            this.router.navigateByUrl(DEFAULT_URL())
        }finally {
            this.loading = false
        }
    }

    // the server gives the latest first
    async loadActivityData() {
        const activity:any[] = await this.repoService.callSingleAPI(this.repoService.getData("api/admin/activity"), COMPONENT, "Load admin activity data", this.userService.getUser())
        this.activityData = (activity || []).map(a => ({ ...a, who: a.byAdmin ? 'LINQ admin' : 'Customer' }))
    }

    private createData() {
        const organisations:any[] = this.analyticsData.organisations
        const totals = organisations.reduce((t, o) => {
            if (o.isPartner) t.numPartners++
            if (o.state === ORGANISATION_STATE.EXPIRED) t.numExpired++
            if (o.state === ORGANISATION_STATE.NEAR_EXPIRY) t.numAlmostExpired++
            t.numModels += o.numModels
            t.actionNodes += o.actionNodes
            t.cost += o.cost
            t.carbon += o.carbon
            t.processingErrors += o.processingErrors
            return t
        }, {numPartners: 0, numExpired:0, numAlmostExpired:0, numModels:0, actionNodes: 0, cost: 0, carbon: 0, processingErrors: 0})

        this.cards = [
            new InsightCard("Organisations", null, 'Number of organisations', null, organisations.length),
            new InsightCard("Users", null, 'Number of users', null, this.analyticsData.userCount),
            new InsightCard("Partners", null, 'Number of partner organisations', null, totals.numPartners),
            new InsightCard("Expired", null, 'Organisations whose account has expired', null, totals.numExpired),
            new InsightCard("Almost expired", null, 'Organisations whose account ends soon', null, totals.numAlmostExpired),
            new InsightCard("Models", null, 'Number of current state models', null, totals.numModels),
            new InsightCard("Action nodes", null, 'Number of action nodes', null, totals.actionNodes),
            new InsightCard("Total cost", null, 'Total modelled cost', null, totals.cost),
            new InsightCard("Total carbon", null, 'Total modelled carbon emissions', null, totals.carbon),
            new InsightCard("Processing errors", null, 'Total number of processing errors', null, totals.processingErrors),
        ]

        const analytics:ModelAnalytics[] = this.analyticsData?.analytics || []
        this.lineData = this.analyticsService.loadAnalyticsDataForChart(analytics).changes
        this.chartDays = analyticsByDay(analytics).days
    }

    private get organisationNames():{[key:string]:string} {
        return Object.fromEntries((this.analyticsData?.organisations || []).map((o:any) => [o._key, o.name]))
    }

    // ---------- a day on the statistics chart ----------

    selectDay(index:number) {
        const day = this.chartDays[index]
        if (!day) return
        this.selectedDay = day
        this.dayChanges = changesOnDay(this.analyticsData?.analytics || [], day, this.organisationNames)
        this.dayOrganisations = organisationsOnDay(this.dayChanges)
    }

    clearDay() {
        this.selectedDay = undefined
        this.dayChanges = []
        this.dayOrganisations = []
    }

    get selectedDayLabel() {
        return this.selectedDay ? formatDate(this.selectedDay, 'EEEE d MMMM y', this.locale) : ''
    }

    // ---------- an organisation's usage ----------

    async handleRowEvent(rowEvent:{row:any, column?:Column}) {
        if (rowEvent.row) await this.selectOrganisation(rowEvent.row)
    }

    async handleDblClickEvent(rowEvent:{row:any, column?:Column}) {
        if (rowEvent.row) this.navigateToOrg(rowEvent.row)
    }

    // from the list of a day's changes
    async selectOrganisationByKey(organisationKey:string) {
        const organisation = (this.analyticsData?.organisations || []).find((o:any) => o._key === organisationKey)
        if (organisation) await this.selectOrganisation(organisation)
    }

    async selectOrganisation(organisation:any) {
        this.selectedOrganisation = organisation
        this.usage = undefined
        this.usageError = ""
        this.loadingUsage = true
        setTimeout(() => this.usagePanel?.nativeElement?.scrollIntoView({behavior: 'smooth', block: 'start'}))
        try {
            const [usage, users] = await Promise.all([
                this.repoService.callSingleAPI(this.repoService.getData<OrganisationUsage>(`api/admin/organisations/${organisation._key}/usage`), COMPONENT, "Load organisation usage", this.userService.getUser(), undefined, undefined, undefined, false),
                this.repoService.callSingleAPI(this.repoService.getData<User[]>(`api/admin/organisations/${organisation._key}/users`), COMPONENT, "Select organsation (admin)", this.userService.getUser()),
            ])
            // a quicker click on another organisation wins
            if (this.selectedOrganisation !== organisation) return
            this.users = users
            this.showUsage(usage)
            // and again once it's drawn, as the page above can move while it loads
            setTimeout(() => this.usagePanel?.nativeElement?.scrollIntoView({behavior: 'smooth', block: 'start'}))
        }catch(err) {
            if (this.selectedOrganisation === organisation) this.usageError = "The organisation's usage couldn't be loaded."
        }finally {
            if (this.selectedOrganisation === organisation) this.loadingUsage = false
        }
    }

    private showUsage(usage:OrganisationUsage) {
        const now = new Date()
        this.usage = usage
        this.summary = summariseUsage(usage, now)
        this.points = talkingPoints(usage, this.summary, now)
        const s = this.summary
        this.usageCards = [
            new InsightCard("Users", null, 'People in the organisation', null, s.users),
            new InsightCard("Active users", null, 'Used LINQ here during the last 30 days', null, s.activeUsers30),
            new InsightCard("Changes (7 days)", null, 'Model changes saved in the last 7 days', null, s.changes7),
            new InsightCard("Changes (30 days)", null, 'Model changes saved in the last 30 days', null, s.changes30),
            new InsightCard("Days since a change", null, 'Days since a model was last changed', s.daysSinceChange === undefined ? 'Never' : null, s.daysSinceChange ?? null),
            new InsightCard("Models", null, 'Models in current state and projects', null, s.models),
            new InsightCard("Projects", null, 'Projects', null, s.projects),
            new InsightCard("Action nodes", null, 'Action nodes in current state', null, s.actionNodes),
        ]

        const daily = analyticsByDay(usage.analytics, now, usage.days)
        const line = new LineData()
        line.labels = daily.days.map(d => formatDate(d, "dd/MM", this.locale))
        line.addLine("Nodes added", daily.newNodes)
        line.addLine("Nodes changed", daily.nodeChanges)
        line.addLine("Nodes deleted", daily.deletedNodes)
        this.usageLineData = line

        this.usageChanges = (usage.analytics || []).map(a => changeRow(a, usage.organisation.name)).sort((a, b) => b.time.getTime() - a.time.getTime())
        this.usageActivity = customerActivity(usage)
        this.usageModels = usage.models.map(m => ({ ...m, where: m.inProject ? 'Project' : 'Current state' }))
            .sort((a, b) => new Date(b.lastChanged || 0).getTime() - new Date(a.lastChanged || 0).getTime())
        this.usageUsers = usage.users.map(u => ({ ...u, role: u.access ? getUserRoleName(u.access) : '', lastActive: u.lastActive ?? u.signInDate, status: !u.activated ? 'Not activated' : (u.lastActive ?? u.signInDate) ? 'Active' : 'Not used yet' }))
            .sort((a, b) => new Date(b.lastActive || 0).getTime() - new Date(a.lastActive || 0).getTime())
    }

    closeUsage() {
        this.selectedOrganisation = undefined
        this.usage = undefined
        this.users = []
    }

    navigateToOrg(organisation:any) {
        this.router.navigateByUrl(DEFAULT_URL(organisation._key))
    }
}
